<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from "vue";
import { useRoute } from "vue-router";

import hopeAvatarSrc from "@/assets/hope/hope-with-background.png";
import ButtonAtom from "@/common/atoms/ButtonAtom.vue";
import { MOBILE_BREAKPOINT } from "@/constants/breakpoints";
import { useStore } from "@/stores";
import { getTourAnchor, type TourAnchorId } from "@/utils/tour_anchors";

// Engineering override: fixed geometry, no spotlight/card layout tokens exist in design-tokens.scss.
const HOLE_PADDING = 8;
const CARD_WIDTH = 360;
const CARD_GAP = 16;
const VIEWPORT_MARGIN = 16;
const GRACE_MS = 3000;

type Placement = "right" | "left" | "bottom" | "bottom-end" | "center";
type StepId =
  | "welcome"
  | "jobs-nav"
  | "new-job"
  | "new-job-menu"
  | "job-card-edit"
  | "job-card"
  | "ask-entry"
  | "ask-composer"
  | "done";

interface TourStep {
  id: StepId;
  anchorId?: TourAnchorId;
  title: string;
  body: string;
  placement: Placement;
  // On button-driven steps; reactive steps show `hint` instead while a watch advances them.
  advanceLabel?: string;
  hint?: string;
  blockHole?: boolean;
  fallbackStep?: StepId;
}

// Fixed step order; content (steps computed below) depends on agentName but ordering never does.
const STEP_ORDER: StepId[] = [
  "welcome",
  "jobs-nav",
  "new-job",
  "new-job-menu",
  "job-card-edit",
  "job-card",
  "ask-entry",
  "ask-composer",
  "done",
];
const stepIndexOf = (id: StepId): number => STEP_ORDER.indexOf(id);
const numberedTotal = STEP_ORDER.length - 2;

const route = useRoute();
const store = useStore();

const agentName = computed(() => store.getters["subscription/agentName"]);

const steps = computed<TourStep[]>(() => [
  {
    id: "welcome",
    title: "Welcome to Blucor!",
    body: "Let's take a quick tour: where your jobs live, and how to get answers when you're stuck. It takes about a minute.",
    placement: "center",
    advanceLabel: "Start tour",
  },
  {
    id: "jobs-nav",
    anchorId: "jobs-nav",
    title: "Your jobs live here",
    body: "Everything starts from **Jobs**. Click it to see the positions you're in charge of.",
    placement: "right",
    hint: "Click the highlighted area to continue",
  },
  {
    id: "new-job",
    anchorId: "new-job",
    title: "Create a new job",
    body: "When you want to add a position, click **New job**.",
    placement: "bottom-end",
    hint: "Click the highlighted area to continue",
    fallbackStep: "jobs-nav",
  },
  {
    id: "new-job-menu",
    anchorId: "new-job-menu",
    title: "Three ways to create",
    body: "**Import from ATS** pulls a job straight from your ATS. **Create manually** builds one from scratch. Or let AI draft it for you with **Create with AI**.",
    placement: "left",
    advanceLabel: "Got it",
    blockHole: true,
    fallbackStep: "new-job",
  },
  {
    id: "job-card-edit",
    anchorId: "job-card-edit",
    title: "Manage a running job",
    body: "To change a job's settings or tune the AI interviewer, click **Edit**, then **Edit AI agent setup**.",
    placement: "bottom",
    advanceLabel: "Next",
    blockHole: true,
    fallbackStep: "jobs-nav",
  },
  {
    id: "job-card",
    anchorId: "job-card",
    title: "See your candidates",
    body: "Click any job card to see its candidates and screening results.",
    placement: "right",
    advanceLabel: "Next",
    blockHole: true,
    fallbackStep: "jobs-nav",
  },
  {
    id: "ask-entry",
    anchorId: "ask-entry",
    title: `Stuck? Ask ${agentName.value}`,
    body: `${agentName.value} can answer any question about the product. Click **Ask ${agentName.value}** to open the chat.`,
    placement: "right",
    hint: "Click the highlighted area to continue",
  },
  {
    id: "ask-composer",
    anchorId: "ask-composer",
    title: "Try asking something",
    body: `Type any question, like "How do I invite candidates?", and hit send.`,
    placement: "left",
    hint: "Send a message to finish the tour",
    fallbackStep: "ask-entry",
  },
  {
    id: "done",
    title: "You're all set!",
    body: `${agentName.value} is always here in the corner. Keep asking questions as you go. That's the fastest way to learn Blucor.`,
    placement: "center",
    advanceLabel: "Finish",
  },
]);

const fetchState = computed(
  () => store.state.onboardingTour?.fetchState ?? "idle",
);
const pending = computed(() => store.state.onboardingTour?.pending ?? false);
const forced = computed(() => store.state.onboardingTour?.forced ?? false);
const completeError = computed(
  () => store.state.onboardingTour?.completeError ?? false,
);
const completing = computed(
  () => store.state.onboardingTour?.completing ?? false,
);
const askPanelOpen = computed(
  () => store.state.helpCenterChat?.isOpen ?? false,
);
const askMessages = computed(() => store.state.helpCenterChat?.messages ?? []);

const active = ref(false);
const paused = ref(false);
const currentStepId = ref<StepId>("welcome");
const finishedThisSession = ref(false);
const messageBaseline = ref(0);

const currentStep = computed<TourStep | null>(() =>
  active.value
    ? (steps.value.find((s) => s.id === currentStepId.value) ?? null)
    : null,
);
const isCentered = computed(() => currentStep.value?.placement === "center");
const stepNumber = computed(() => {
  const idx = stepIndexOf(currentStepId.value);
  return idx > 0 && idx < STEP_ORDER.length - 1 ? idx : 0;
});

// Track the breakpoint reactively (not a one-time read): PositionList gates its sample card on the same reactive
// `isMobile`, so a mobile→desktop resize must flip tour eligibility too, or the two desync (card shows, tour can't start).
const viewportOk = ref(window.innerWidth > MOBILE_BREAKPOINT);
const viewportQuery = window.matchMedia(
  `(min-width: ${MOBILE_BREAKPOINT + 1}px)`,
);
const onViewportChange = (event: MediaQueryListEvent) => {
  viewportOk.value = event.matches;
};
viewportQuery.addEventListener("change", onViewportChange);

let graceRafId = 0;
// `null` (not 0) marks "unset": a real rAF timestamp can legitimately be 0.
let graceStartTs: number | null = null;

const cancelGrace = () => {
  if (graceRafId) cancelAnimationFrame(graceRafId);
  graceRafId = 0;
  graceStartTs = null;
};

const onGraceExpired = () => {
  const step = currentStep.value;
  if (!step) return;
  if (step.fallbackStep) {
    goToStep(step.fallbackStep);
  } else {
    // No fallback (anchor is always-on-page, e.g. sidebar): hide until it returns rather than jumping.
    paused.value = true;
  }
};

const tickGrace = (ts: number) => {
  if (graceStartTs === null) graceStartTs = ts;
  if (ts - graceStartTs >= GRACE_MS) {
    graceRafId = 0;
    onGraceExpired();
    return;
  }
  graceRafId = requestAnimationFrame(tickGrace);
};

const startGrace = () => {
  if (graceRafId) return;
  graceStartTs = null;
  graceRafId = requestAnimationFrame(tickGrace);
};

const goToStep = (id: StepId) => {
  cancelGrace();
  paused.value = false;
  currentStepId.value = id;
};

const finish = async () => {
  if (completing.value) return;
  await store.dispatch("onboardingTour/complete");
  // completeError is the success truth here (the guard ensured the dispatch ran); pending isn't, since a QA run can start with pending false.
  if (!completeError.value) {
    finishedThisSession.value = true;
    active.value = false;
    cancelGrace();
    // Drop the ?tour=1 latch too, or PositionList's `pending || forced` gate keeps the fake sample card visible after a forced run.
    store.commit("onboardingTour/setForced", false);
  }
};

const advance = () => {
  const idx = stepIndexOf(currentStepId.value);
  if (idx === -1 || idx >= STEP_ORDER.length - 1) {
    finish();
    return;
  }
  goToStep(STEP_ORDER[idx + 1]);
};

const onEnterStep = (id: StepId) => {
  if (id === "jobs-nav" && route.path === "/positions") {
    advance();
    return;
  }
  if (id === "ask-entry" && askPanelOpen.value) {
    goToStep("ask-composer");
    return;
  }
  if (id === "ask-composer") {
    messageBaseline.value = askMessages.value.length;
  }
};

watch(currentStepId, (id) => {
  if (active.value) onEnterStep(id);
});

const start = () => {
  active.value = true;
  currentStepId.value = "welcome";
  paused.value = false;
};

const canAutoStart = computed(
  () =>
    fetchState.value === "loaded" &&
    viewportOk.value &&
    !finishedThisSession.value &&
    !active.value,
);
const effectivePending = computed(() => forced.value || pending.value);

watch(
  [canAutoStart, effectivePending],
  ([can, eff]) => {
    if (can && eff) start();
  },
  { immediate: true },
);

// QA latch: ?tour=1 arms one forced run (stored so PositionList's sample-card gate sees it too).
// Immediate watcher, not onMounted, because the router may not have resolved the initial URL at mount, and navigation dropping the query must not disarm it.
watch(
  () => route.query.tour,
  (tour) => {
    if (tour !== "1") return;
    store.commit("onboardingTour/setForced", true);
    finishedThisSession.value = false;
  },
  { immediate: true },
);

// jobs-nav advances once the session is on /positions; steps anchored there fall back to it on Back navigation away.
watch(
  () => route.path,
  (path) => {
    if (!active.value) return;
    const id = currentStepId.value;
    if (id === "jobs-nav" && path === "/positions") {
      advance();
      return;
    }
    if (
      (id === "new-job" ||
        id === "new-job-menu" ||
        id === "job-card-edit" ||
        id === "job-card") &&
      path !== "/positions"
    ) {
      goToStep("jobs-nav");
    }
  },
);

// The New-job button has no click listener; its menu registering "new-job-menu" is the signal the click happened.
watch(
  () => getTourAnchor("new-job-menu"),
  (el) => {
    if (active.value && currentStepId.value === "new-job" && el) advance();
  },
);

watch(askPanelOpen, (isOpen) => {
  if (!active.value) return;
  if (currentStepId.value === "ask-entry" && isOpen) {
    goToStep("ask-composer");
  } else if (currentStepId.value === "ask-composer" && !isOpen) {
    goToStep("ask-entry");
  }
});

watch(
  () => askMessages.value.length,
  (len) => {
    if (!active.value || currentStepId.value !== "ask-composer") return;
    if (len <= messageBaseline.value) return;
    const last = askMessages.value[len - 1];
    if (last?.isHuman) advance();
  },
);

const currentAnchorEl = computed<HTMLElement | undefined>(() => {
  const anchorId = currentStep.value?.anchorId;
  return anchorId ? getTourAnchor(anchorId) : undefined;
});

// Key on currentStepId too, and run immediately: if an anchored step's anchor never registers, currentAnchorEl stays undefined -> undefined,
// so a currentAnchorEl-only watch never fires and the grace/fallback never starts, stranding the tour.
watch(
  [currentAnchorEl, currentStepId],
  ([el]) => {
    if (!active.value || !currentStep.value?.anchorId) return;
    if (el) {
      cancelGrace();
      paused.value = false;
    } else {
      startGrace();
    }
  },
  { immediate: true },
);

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}
const targetRect = ref<Rect | null>(null);
const cardEl = ref<HTMLElement | null>(null);
let rafId = 0;

// rAF measures the anchor's rect only (no DOM queries/listeners), so the spotlight tracks layout/scroll motion with it.
const track = () => {
  const anchorId = currentStep.value?.anchorId;
  const el = anchorId ? getTourAnchor(anchorId) : undefined;
  if (el) {
    const r = el.getBoundingClientRect();
    const next_ = {
      top: r.top,
      left: r.left,
      width: r.width,
      height: r.height,
    };
    const prev = targetRect.value;
    if (
      !prev ||
      Math.abs(prev.top - next_.top) > 0.5 ||
      Math.abs(prev.left - next_.left) > 0.5 ||
      Math.abs(prev.width - next_.width) > 0.5 ||
      Math.abs(prev.height - next_.height) > 0.5
    ) {
      targetRect.value = next_;
    }
  } else {
    targetRect.value = null;
  }
  rafId = requestAnimationFrame(track);
};

// Immediate: canAutoStart's watch above can flip `active` true in its own earlier immediate run, before this watcher exists.
watch(
  active,
  (isActive) => {
    cancelAnimationFrame(rafId);
    if (isActive) {
      rafId = requestAnimationFrame(track);
    } else {
      targetRect.value = null;
    }
  },
  { immediate: true },
);

onUnmounted(() => {
  cancelAnimationFrame(rafId);
  cancelGrace();
  viewportQuery.removeEventListener("change", onViewportChange);
});

const holeRect = computed<Rect | null>(() => {
  if (!targetRect.value) return null;
  return {
    top: targetRect.value.top - HOLE_PADDING,
    left: targetRect.value.left - HOLE_PADDING,
    width: targetRect.value.width + HOLE_PADDING * 2,
    height: targetRect.value.height + HOLE_PADDING * 2,
  };
});

const holeStyle = computed(() => {
  const r = holeRect.value;
  if (!r) return {};
  return {
    top: `${r.top}px`,
    left: `${r.left}px`,
    width: `${r.width}px`,
    height: `${r.height}px`,
  };
});

const blockerStyles = computed(() => {
  const r = holeRect.value;
  if (!r) return null;
  const right = r.left + r.width;
  const bottom = r.top + r.height;
  return {
    top: { top: "0", left: "0", right: "0", height: `${Math.max(r.top, 0)}px` },
    bottom: { top: `${bottom}px`, left: "0", right: "0", bottom: "0" },
    left: {
      top: `${r.top}px`,
      left: "0",
      width: `${Math.max(r.left, 0)}px`,
      height: `${r.height}px`,
    },
    right: {
      top: `${r.top}px`,
      left: `${right}px`,
      right: "0",
      height: `${r.height}px`,
    },
    hole: {
      top: `${r.top}px`,
      left: `${r.left}px`,
      width: `${r.width}px`,
      height: `${r.height}px`,
    },
  };
});

const cardStyle = computed(() => {
  const r = holeRect.value;
  if (!r) return {};
  const cardHeight = cardEl.value?.offsetHeight ?? 200;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let top: number;
  let left: number;
  switch (currentStep.value?.placement) {
    case "right":
      top = r.top;
      left = r.left + r.width + CARD_GAP;
      break;
    case "left":
      top = r.top;
      left = r.left - CARD_WIDTH - CARD_GAP;
      break;
    case "bottom-end":
      top = r.top + r.height + CARD_GAP;
      left = r.left + r.width - CARD_WIDTH;
      break;
    case "bottom":
    default:
      top = r.top + r.height + CARD_GAP;
      left = r.left;
      break;
  }
  left = Math.min(
    Math.max(left, VIEWPORT_MARGIN),
    vw - CARD_WIDTH - VIEWPORT_MARGIN,
  );
  top = Math.min(
    Math.max(top, VIEWPORT_MARGIN),
    vh - cardHeight - VIEWPORT_MARGIN,
  );
  return { top: `${top}px`, left: `${left}px`, width: `${CARD_WIDTH}px` };
});

// Renders `**bold**` runs without v-html.
interface TextRun {
  text: string;
  bold: boolean;
}
const bodyRuns = computed<TextRun[]>(() => {
  const body = currentStep.value?.body ?? "";
  return body
    .split("**")
    .map((text, i) => ({ text, bold: i % 2 === 1 }))
    .filter((run) => run.text.length > 0);
});
</script>

<template>
  <Teleport to="body">
    <template v-if="active && !paused && currentStep">
      <div v-if="isCentered" class="tour-scrim" />
      <div v-else-if="holeRect" class="tour-hole" :style="holeStyle" />
      <template v-if="!isCentered && blockerStyles">
        <div
          class="tour-blocker"
          :style="blockerStyles.top"
          @click.stop
          @pointerdown.stop
        />
        <div
          class="tour-blocker"
          :style="blockerStyles.bottom"
          @click.stop
          @pointerdown.stop
        />
        <div
          class="tour-blocker"
          :style="blockerStyles.left"
          @click.stop
          @pointerdown.stop
        />
        <div
          class="tour-blocker"
          :style="blockerStyles.right"
          @click.stop
          @pointerdown.stop
        />
        <div
          v-if="currentStep.blockHole"
          class="tour-blocker tour-blocker--hole"
          :style="blockerStyles.hole"
          @click.stop
          @pointerdown.stop
        />
      </template>

      <div v-if="isCentered" class="tour-center">
        <div ref="cardEl" class="tour-card">
          <img :src="hopeAvatarSrc" alt="" class="tour-card__avatar" />
          <div class="tour-card__content">
            <h3 class="tour-card__title">{{ currentStep.title }}</h3>
            <p class="tour-card__body">
              <template v-for="(run, i) in bodyRuns" :key="i">
                <strong v-if="run.bold">{{ run.text }}</strong>
                <template v-else>{{ run.text }}</template>
              </template>
            </p>
            <p
              v-if="currentStep.id === 'done' && completeError"
              class="tour-card__error"
            >
              Couldn't save that you finished the tour. Try again.
            </p>
            <div class="tour-card__actions">
              <ButtonAtom
                :label="currentStep.advanceLabel"
                variant="primary"
                size="sm"
                :loading="completing"
                @click="advance"
              />
            </div>
          </div>
        </div>
      </div>

      <div
        v-else-if="holeRect"
        ref="cardEl"
        class="tour-card tour-card--anchored"
        :style="cardStyle"
      >
        <img :src="hopeAvatarSrc" alt="" class="tour-card__avatar" />
        <div class="tour-card__content">
          <div class="tour-card__step">
            Step {{ stepNumber }} of {{ numberedTotal }}
          </div>
          <h3 class="tour-card__title">{{ currentStep.title }}</h3>
          <p class="tour-card__body">
            <template v-for="(run, i) in bodyRuns" :key="i">
              <strong v-if="run.bold">{{ run.text }}</strong>
              <template v-else>{{ run.text }}</template>
            </template>
          </p>
          <div class="tour-card__actions">
            <ButtonAtom
              v-if="currentStep.advanceLabel"
              :label="currentStep.advanceLabel"
              variant="primary"
              size="sm"
              @click="advance"
            />
            <span v-else class="tour-card__hint">{{ currentStep.hint }}</span>
          </div>
        </div>
      </div>
    </template>
  </Teleport>
</template>

<style scoped lang="scss">
@use "@/assets/styles/design-tokens" as *;

// Above the app's z-index max (13000): scrim/hole, blockers, blocked-hole cover, then the card on top so it stays interactive.
$tour-z-scrim: 14000;
$tour-z-blocker: 14010;
$tour-z-hole-cover: 14020;
$tour-z-card: 14030;

.tour-scrim {
  position: fixed;
  inset: 0;
  // Engineering override: 0.55 scrim alpha, no dim-overlay token exists.
  background: rgba($color-content-default, 0.55);
  z-index: $tour-z-scrim;
}

.tour-hole {
  position: fixed;
  z-index: $tour-z-scrim;
  border-radius: $radius-md;
  border: $focus-ring-width solid $color-content-primary;
  // Engineering override: 0.55 scrim alpha, no dim-overlay token exists.
  box-shadow: 0 0 0 200vmax rgba($color-content-default, 0.55);
  pointer-events: none;
  transition:
    top 0.25s ease,
    left 0.25s ease,
    width 0.25s ease,
    height 0.25s ease;
  animation: tour-pulse 1.6s ease-in-out infinite;
}

@keyframes tour-pulse {
  0%,
  100% {
    outline: 0 solid rgba($color-content-primary, 0.35);
  }
  50% {
    // Engineering override: fixed 6px outline width, no outline-width token exists.
    outline: 6px solid rgba($color-content-primary, 0.35);
  }
}

.tour-blocker {
  position: fixed;
  z-index: $tour-z-blocker;
}

.tour-blocker--hole {
  z-index: $tour-z-hole-cover;
}

.tour-center {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: $tour-z-card;
}

.tour-card {
  background: $color-surface-default;
  border-radius: $radius-lg;
  box-shadow: $shadow-modal;
  padding: $spacing-6;
  // Engineering override: fixed card width, no width token exists in design-tokens.scss.
  width: 400px;
  display: flex;
  flex-direction: row;
  gap: $spacing-3;
}

.tour-card__avatar {
  // Engineering override: fixed avatar size, matches the mock; no size token exists.
  width: 40px;
  height: 40px;
  border-radius: $radius-full;
  flex-shrink: 0;
}

.tour-card__content {
  display: flex;
  flex-direction: column;
  gap: $spacing-2;
  min-width: 0;
}

.tour-card--anchored {
  position: fixed;
  z-index: $tour-z-card;
}

.tour-card__step {
  @include txt-xsmall-plus;
  color: $color-content-primary;
  text-transform: uppercase;
  // Engineering override: fixed 1px letter-spacing, no letter-spacing token exists.
  letter-spacing: 1px;
}

.tour-card__title {
  @include txt-medium-plus;
  color: $color-content-default;
  margin: 0;
}

.tour-card__body {
  @include txt-small;
  color: $color-content-subtle;
  margin: 0;

  strong {
    color: $color-content-default;
  }
}

.tour-card__error {
  @include txt-xsmall;
  color: $color-red-60;
  margin: 0;
}

.tour-card__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $spacing-3;
  margin-top: $spacing-3;
}

.tour-card__hint {
  @include txt-xsmall;
  color: $color-content-primary;
}
</style>
